import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import Alert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import { QueryState } from "@/components/common/AsyncState";
import { createWriteOff, fetchWriteOffs, searchOpenCharges } from "@/api/finance";
import { fetchStudents } from "@/api/students";
import { formatDate, formatMoney, periodLabel } from "@/utils/format";
import { extractErrorMessage } from "@/types/common";
import type { Student } from "@/types/student";

function CreateWriteOffDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [studentQuery, setStudentQuery] = useState("");
  const [student, setStudent] = useState<Student | null>(null);
  const [chargeId, setChargeId] = useState("");
  const [amount, setAmount] = useState<number>(0);
  const [reason, setReason] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  const { data: studentsPage } = useQuery({
    queryKey: ["students", "search", studentQuery],
    queryFn: () => fetchStudents({ search: studentQuery, limit: 20 }),
    enabled: studentQuery.length > 1,
  });

  const { data: charges } = useQuery({
    queryKey: ["open-charges", student?.id],
    queryFn: () => searchOpenCharges(student!.id),
    enabled: Boolean(student),
  });

  const mutation = useMutation({
    mutationFn: () => createWriteOff({ chargeId, amount, reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["write-offs"] });
      void queryClient.invalidateQueries({ queryKey: ["debtors"] });
      setStudent(null);
      setChargeId("");
      setAmount(0);
      setReason("");
      onClose();
    },
    onError: (error) => setServerError(extractErrorMessage(error)),
  });

  const selectedCharge = (charges ?? []).find((c) => c.id === chargeId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Списание долга</DialogTitle>
      <DialogContent>
        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        )}
        <Stack spacing={2} mt={1}>
          <Autocomplete
            options={studentsPage?.items ?? []}
            getOptionLabel={(option) => `${option.fullName} — ${option.phone}`}
            value={student}
            onChange={(_, value) => {
              setStudent(value);
              setChargeId("");
            }}
            onInputChange={(_, value) => setStudentQuery(value)}
            renderInput={(params) => <TextField {...params} label="Ученик" placeholder="Начните вводить ФИО или телефон" />}
          />
          {student && (
            <TextField select label="Начисление (период)" value={chargeId} onChange={(e) => setChargeId(e.target.value)}>
              {(charges ?? []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {periodLabel(c.period)} — остаток {formatMoney(Math.max(0, c.left ?? c.amount))}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            label="Сумма списания"
            type="number"
            value={amount || ""}
            onChange={(e) => setAmount(Number(e.target.value))}
            helperText={selectedCharge ? `Остаток по начислению: ${formatMoney(Math.max(0, selectedCharge.left ?? selectedCharge.amount))}` : undefined}
          />
          <TextField label="Причина списания" value={reason} onChange={(e) => setReason(e.target.value)} multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button
          variant="contained"
          disabled={!student || !chargeId || !amount || reason.trim().length < 2 || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          Списать
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function WriteOffsTab() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["write-offs"],
    queryFn: () => fetchWriteOffs(),
  });

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Списать долг
        </Button>
      </Stack>
      <QueryState isLoading={isLoading} error={error}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Дата</TableCell>
              <TableCell>Ученик</TableCell>
              <TableCell>Филиал</TableCell>
              <TableCell>Период</TableCell>
              <TableCell>Сумма</TableCell>
              <TableCell>Причина</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data ?? []).map((row) => (
              <TableRow key={row.id}>
                <TableCell>{formatDate(row.createdAt)}</TableCell>
                <TableCell>{row.studentName}</TableCell>
                <TableCell>{row.branchName}</TableCell>
                <TableCell>{periodLabel(row.period)}</TableCell>
                <TableCell>{formatMoney(row.amount)}</TableCell>
                <TableCell>{row.reason}</TableCell>
              </TableRow>
            ))}
            {(data ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Списаний пока нет.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </QueryState>
      <CreateWriteOffDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
}
